import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import FieldError from 'ui-library/FieldError';

import {SchemaFieldSelectorOptionInputQuery} from './__generated/SchemaFieldSelectorOptionInputQuery';
import BaseSubdependencyList from './BaseSubdependencyList';
import {ChildDependencyTypes} from './ChildDependencyTypes';

export const SCHEMA_FIELD_SELECTOR_OPTION_INPUT_QUERY = gql`
  query SchemaFieldSelectorOptionInputQuery($id: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    schemaFieldSelector(id: $id) {
      ... on SchemaFieldSelectorNode {
        ...SchemaFieldSelectorGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function SchemaFieldSelectorOptionInput({
  subdependencies,
  onChange,
  dependencyValidation,
  updateDependencyConfigValidation,
  disabled,
  parentDependencySelectedId,
}: ChildDependencyTypes): JSX.Element | null {
  let schemaFieldSelector = null;
  const result = useSkippableBenchlingQuery<SchemaFieldSelectorOptionInputQuery>(
    SCHEMA_FIELD_SELECTOR_OPTION_INPUT_QUERY,
    {
      skip: parentDependencySelectedId == null,
      variables: {id: parentDependencySelectedId},
    }
  );
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return (
      <BaseSubdependencyList
        subdependencies={subdependencies}
        onChange={onChange}
        dependencyValidation={dependencyValidation}
        updateDependencyConfigValidation={updateDependencyConfigValidation}
        disabled={disabled}
        isLoading={true}
        subdependencyOptions={[]}
        subdependencyType="option"
        parentdependencyType="Dropdown"
      />
    );
  } else if (result.type === QueryResultType.SKIPPED) {
    return null;
  } else if (isInaccessible(result.data.schemaFieldSelector)) {
    return <FieldError message="Selected dropdown is inaccessible." />;
  } else {
    schemaFieldSelector = result.data.schemaFieldSelector;
  }

  return (
    <BaseSubdependencyList
      subdependencies={subdependencies}
      onChange={onChange}
      dependencyValidation={dependencyValidation}
      updateDependencyConfigValidation={updateDependencyConfigValidation}
      disabled={disabled}
      isLoading={false}
      subdependencyOptions={schemaFieldSelector.schemaFieldSelectorOptions}
      subdependencyType="option"
      parentdependencyType="Dropdown"
    />
  );
}
